/* eslint-disable react-hooks/exhaustive-deps */
import {
  faExclamationTriangle,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import Select, { MultiValue } from "react-select";
import {
  useUpdateUsersInGroupMutation,
  useLazyDeleteGroupQuery,
  useUpdateSubGroupsInGroupMutation,
} from "../../../services/thoughtSpotV1";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { ReactSelectOption } from "../../Interfaces";

interface UsersAndGroupsInGroupSelectorProps {
  orgId: number;
  group: SingleInterface;
  selectableUsers: MultiplesInterface;
  selectableGroups: MultiplesInterface;
  preselectedMultiplesUsers: ReactSelectOption[];
  preselectedMultiplesGroups: ReactSelectOption[];
  tsAccessToken: string;
  deletionCallback: () => any;
}

interface SingleInterface {
  text: string;
  description?: string;
  content: ReactSelectOption;
  internalTSLabel: string;
}

interface MultiplesInterface {
  text: string;
  content: ReactSelectOption[];
}

interface UsersAndGroupsInGroupSelectorState {
  selectedMultiplesUsers: ReactSelectOption[];
  compareMultiplesUsers: ReactSelectOption[];
  selectedMultiplesGroups: ReactSelectOption[];
  compareMultiplesGroups: ReactSelectOption[];
  openPermissionsMenu: boolean;
  enableSaveButton: boolean;
  samePermissions: boolean;
  confirmingDeletion: boolean;
  loading: boolean;
  deleteConfirmationModaltoggle: boolean;
  deleteGroup: string;
  error: {
    errorFlag: boolean;
    errorMessage: string;
  };
}

export const UsersAndGroupsInGroupSelector = (
  props: UsersAndGroupsInGroupSelectorProps,
) => {
  const [state, setState] = useState<UsersAndGroupsInGroupSelectorState>({
    selectedMultiplesUsers: props.preselectedMultiplesUsers,
    selectedMultiplesGroups: props.preselectedMultiplesGroups,
    openPermissionsMenu: true,
    enableSaveButton: true,
    samePermissions: true,
    confirmingDeletion: false,
    loading: false,
    compareMultiplesUsers: props.preselectedMultiplesUsers,
    compareMultiplesGroups: props.preselectedMultiplesGroups,
    deleteConfirmationModaltoggle: false,
    deleteGroup: "",
    error: { errorFlag: false, errorMessage: "" },
  });

  const [updateUsersInGroupQuery, updateUsersInGroupQueryResults] =
    useUpdateUsersInGroupMutation();

  const [updateSubGroupsInGroupQuery, updateSubGroupsInGroupQueryResults] =
    useUpdateSubGroupsInGroupMutation();

  const [deleteGroupQuery, deleteGroupQueryResults] = useLazyDeleteGroupQuery();

  const handleChangeUsers = (
    selectedOptions: MultiValue<ReactSelectOption>,
  ) => {
    if (Array.isArray(selectedOptions)) {
      if (
        JSON.stringify(selectedOptions) ===
        JSON.stringify(state.compareMultiplesUsers)
      ) {
        // need to stringify to compare
        setState({
          ...state,
          selectedMultiplesUsers: state.compareMultiplesUsers,
          samePermissions: true,
        });
      } else {
        setState({
          ...state,
          selectedMultiplesUsers: selectedOptions,
          compareMultiplesUsers: selectedOptions,
          samePermissions: false,
        });
      }
    }
  };

  const handleChangeGroups = (
    selectedOptions: MultiValue<ReactSelectOption>,
  ) => {
    if (Array.isArray(selectedOptions)) {
      if (
        JSON.stringify(selectedOptions) ===
        JSON.stringify(state.compareMultiplesGroups)
      ) {
        // need to stringify to compare
        setState({
          ...state,
          selectedMultiplesGroups: state.compareMultiplesGroups,
          samePermissions: true,
        });
      } else {
        setState({
          ...state,
          selectedMultiplesGroups: selectedOptions,
          compareMultiplesGroups: selectedOptions,
          samePermissions: false,
        });
      }
    }
  };

  const savePermissions = async () => {
    setState((prevState) => ({
      ...prevState,
      enableSaveButton: false,
      loading: true,
    }));
    updateUsersInGroupQuery({
      thoughtSpotAccessToken: props.tsAccessToken,
      groupId: props.group.content.value,
      userIdArray: reactSelectToStringArray(state.selectedMultiplesUsers),
    });
    updateSubGroupsInGroupQuery({
      thoughtSpotAccessToken: props.tsAccessToken,
      groupId: props.group.content.value,
      groupsIdArray: reactSelectToStringArray(state.selectedMultiplesGroups),
    });
  };

  useEffect(() => {
    if (
      updateUsersInGroupQueryResults.isSuccess &&
      updateUsersInGroupQueryResults.status === "fulfilled"
    ) {
      setState((prevState) => ({
        ...prevState,
        enableSaveButton: true,
        samePermissions: true,
        loading: false,
      }));
    }
  }, [updateUsersInGroupQueryResults]);

  const doDeleteGroup = async () => {
    setState((prevState) => ({ ...prevState, loading: true }));
    deleteGroupQuery({
      thoughtSpotAccessToken: props.tsAccessToken,
      groupId: props.group.content.value,
    });
  };

  useEffect(() => {
    if (
      deleteGroupQueryResults.isSuccess &&
      deleteGroupQueryResults.status === "fulfilled"
    ) {
      props.deletionCallback();
    }
  }, [deleteGroupQueryResults]);

  const reactSelectToStringArray = (reactSelectArray: ReactSelectOption[]) => {
    let stringArray: string[] = reactSelectArray.map(
      (element) => element.value,
    );
    return stringArray;
  };

  const toggleDeleteConfirmationModal = () => {
    setState({
      ...state,
      deleteGroup: props.group.content.label,
      deleteConfirmationModaltoggle: !state.deleteConfirmationModaltoggle,
    });
  };

  return (
    <>
      <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
        {state.loading && (
          <div className="module-loading">
            <FontAwesomeIcon
              icon={faSpinner}
              size="2x"
              spin={true}
              style={{ margin: "0 auto" }}
            />
          </div>
        )}
        {!state.loading && (
          <div className="card my-2">
            <div className="card-body">
              <div
                className="d-flex"
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h5 className="card-title">
                    {props.group.text}: {props.group.content.label}
                  </h5>
                  {props.group.description && (
                  <h6 className="card-title">
                    Description: {props.group.description}
                  </h6>
                  )}
                </div>
                <div>
                  <button
                    style={{
                      minWidth: 100,
                      display: !state.confirmingDeletion ? "block" : "none",
                    }}
                    className="btn"
                    onClick={() => toggleDeleteConfirmationModal()}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-1" />
                    Delete
                  </button>
                </div>
              </div>
              <div
                style={{
                  display: state.openPermissionsMenu ? "block" : "none",
                }}
              >
                <div>
                  <div className="card-text">{props.selectableUsers.text}:</div>
                  <Select
                    defaultValue={state.compareMultiplesUsers}
                    onChange={handleChangeUsers}
                    options={props.selectableUsers.content}
                    isMulti={true}
                    className="basic-multi-select card-text"
                    classNamePrefix="select"
                    closeMenuOnSelect={false}
                  />
                  <div className="card-text">
                    {props.selectableGroups.text}:
                  </div>
                  <Select
                    defaultValue={state.compareMultiplesGroups}
                    onChange={handleChangeGroups}
                    options={props.selectableGroups.content}
                    isMulti={true}
                    className="basic-multi-select card-text"
                    classNamePrefix="select"
                    closeMenuOnSelect={false}
                  />
                  {state.error.errorFlag && (
                    <div className="alert alert-danger mt-2">
                      <div className="input-inline-error-message">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="me-1"
                        />
                        Error: {state.error.errorMessage}
                      </div>
                    </div>
                  )}
                  <div style={{ paddingTop: "5px" }}>
                    <button
                      className="btn btn-primary"
                      style={{ width: "100px" }}
                      disabled={
                        !state.enableSaveButton || state.samePermissions
                      }
                      onClick={() => savePermissions()}
                    >
                      {state.enableSaveButton ? "Save" : "Saving"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <Modal
          isOpen={state.deleteConfirmationModaltoggle}
          toggle={toggleDeleteConfirmationModal}
        >
          <ModalHeader toggle={toggleDeleteConfirmationModal}>
            Delete Group
          </ModalHeader>
          <ModalBody>
            Are you sure you want to delete group, {state.deleteGroup}?
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() => {
                toggleDeleteConfirmationModal();
                doDeleteGroup();
              }}
            >
              Yes
            </Button>{" "}
            <Button
              color="secondary"
              onClick={() => toggleDeleteConfirmationModal()}
            >
              No
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};
